/**
 * Frequency cap for the Pro upsell fake-door test: at most once per user per
 * day. Keyed by the app's own identity (the authorized-link token, which is
 * also what App.tsx hands PostHog as the distinct_id -- see its identify()
 * call) rather than a single global key, so two different visitors sharing a
 * browser don't cap each other, and persisted in localStorage rather than
 * component state so a reload or a same-day second download doesn't re-show
 * it either.
 *
 * localStorage is the enforcement mechanism, since it's synchronous and
 * exactly what the acceptance criteria (reload same day => no re-show) test.
 * The card's own `pro_prompt_shown` capture additionally $sets a person
 * property with the same date, which is a best-effort cross-device signal
 * for later analysis -- it is not read back client-side to gate anything,
 * since posthog-js doesn't make arbitrary custom person properties readable
 * back on the client without extra setup.
 */
const PREFIX = "pro_upsell_last_shown_";

/** YYYY-MM-DD in the visitor's own local timezone, not UTC -- "once per day"
 * means their calendar day, not the server's. */
function localDateString(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function hasSeenProUpsellToday(userId: string): boolean {
  if (!userId) return false;
  return localStorage.getItem(PREFIX + userId) === localDateString();
}

export function markProUpsellShown(userId: string): void {
  if (!userId) return;
  localStorage.setItem(PREFIX + userId, localDateString());
}

/**
 * Permanent suppression once someone has clicked "I'm interested" -- separate
 * key from the daily-shown one above, and never expires: this is "don't ask
 * again," not "don't ask again today." For now, per explicit instruction,
 * rather than a real preference the person set -- revisit if that changes.
 */
const INTERESTED_PREFIX = "pro_upsell_interested_";

export function hasClickedProInterested(userId: string): boolean {
  if (!userId) return false;
  return localStorage.getItem(INTERESTED_PREFIX + userId) === "1";
}

export function markProInterested(userId: string): void {
  if (!userId) return;
  localStorage.setItem(INTERESTED_PREFIX + userId, "1");
}
